import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, Zap, TrendingUp, Package, CreditCard } from "lucide-react"
import { supabase } from '@/lib/supabase'
import { PersonalAccountResponse } from './types'
import { useCredits } from '@/hooks/useCredits'
import { CreditUsageBar } from '@/components/ui/credit-usage-bar'
import { CreditPurchaseModal } from '@/components/billing/CreditPurchaseModal'
import { Link } from 'react-router-dom'
import { useAccountStatus } from '@/hooks/useAccountStatus'

export default function BillingSettings() {
    const [loading, setLoading] = useState(false)
    const [showPurchaseModal, setShowPurchaseModal] = useState(false)
    const { balance, loading: creditsLoading, fetchBalance } = useCredits()
    const { refresh } = useAccountStatus()
    
    // Refresh credit balance on mount
    useEffect(() => {
        fetchBalance()
    }, [])

    const handleUpdateBilling = async () => {
        try {
            setLoading(true)

            // Update the RPC call to use the correct typing
            const { data: account } = await supabase.rpc('get_personal_account') as { data: PersonalAccountResponse | null }

            if (!account?.account_id) {
                throw new Error('No account found')
            }


            const session = await supabase.auth.getSession();
            if (!session.data.session) {
                throw new Error('No session found');
            }

            // Use the worker to get the billing portal url
            const workerUrl = import.meta.env.VITE_WORKERS_URL;

            const response = await fetch(`${workerUrl}/billing-functions/get_billing_portal_url`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({
                    account_id: account.account_id,
                    return_url: `${window.location.origin}/settings`
                })
            });
            const data = await response.json();
            console.log('Got billing portal url', data)

            if (data?.url) {
                window.location.href = data.url
            }
        } catch (error) {
            console.error('Error accessing billing portal:', error)
        } finally {
            setLoading(false)
        }
    }

    // Calculate credit usage
    const totalCredits = balance?.total_credits || 0
    const usedCredits = totalCredits - ((balance?.plan_credits || 0) + (balance?.purchased_credits || 0))

    return (
        <div className="space-y-6 pt-8 px-8">
            <div>
                <h2 className="text-2xl font-bold mb-2">Billing & Credits</h2>
                <p className="text-muted-foreground">
                    Manage your subscription, credits, and payment methods
                </p>
            </div>

            {/* Credit Overview */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5" />
                        Credit Balance
                    </CardTitle>
                    <CardDescription>
                        Your current credit usage and balance
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!creditsLoading && balance ? (
                        <>
                            <CreditUsageBar
                                planCredits={balance.plan_credits}
                                purchasedCredits={balance.purchased_credits}
                                totalCredits={balance.total_credits}
                                usedCredits={usedCredits}
                                showDetails={true}
                            />
                            
                            <div className="flex flex-wrap gap-2 pt-2">
                                <Button 
                                    variant="default"
                                    onClick={() => setShowPurchaseModal(true)}
                                >
                                    <Package className="mr-2 h-4 w-4" />
                                    Purchase Credits
                                </Button>
                                <Button variant="outline" asChild>
                                    <Link to="/settings/billing/credits">
                                        <TrendingUp className="mr-2 h-4 w-4" />
                                        View Usage Details
                                    </Link>
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Subscription Management */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Subscription & Payment Methods
                    </CardTitle>
                    <CardDescription>
                        Manage your subscription plan and payment information
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Button
                        onClick={handleUpdateBilling}
                        disabled={loading}
                    >
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Manage Subscription
                    </Button>
                    <p className="text-sm text-muted-foreground">
                        You'll be redirected to the secure billing portal to manage your subscription, payment methods, and view invoice history.
                    </p>
                </CardContent>
            </Card>

            {/* Credit Purchase Modal */}
            <CreditPurchaseModal
                open={showPurchaseModal}
                onOpenChange={setShowPurchaseModal}
                onSuccess={() => {
                    fetchBalance()
                    refresh()
                }}
            />
        </div>
    )
} 